/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/modals/linkedinMessageModal.js
 * @function  openLinkedinMessageModal
 * @layer     ui-modal
 * @summary   Opens a lightweight modal that renders an email template as a
 *            LinkedIn message snippet and lets the seller copy it before
 *            jumping to LinkedIn.
 *
 * @description
 * • Uses the same Mustache-powered TemplateService as the email composer to
 *   hydrate merge variables for the selected template.
 * • Presents the rendered content inside a simple `<textarea>` so the seller
 *   can tweak copy before copying.
 * • Tracks whether the copy button succeeded to feed metadata back into
 *   task completion analytics.
 *
 * ## Public API
 * ```ts
 * openLinkedinMessageModal({
 *   templateId?:   string,
 *   templateIds?:  string[],
 *   templateData?: Record<string, unknown>,
 *   mergeVars?:    Record<string, unknown>,
 *   contactName?:  string,
 *   contactId?:    string,
 *   taskId?:       string,
 *   linkedinUrl?:  string,
 *   channelLabel?: string,
 *   helperMessage?: string,
 *   placeholder?: string,
 *   primaryButtonLabel?: string,
 * }): Promise<{
 *   templateId: string | null,
 *   message: string,
 *   copied: boolean,
 *   copiedAt: number | null,
 *   copyMethod: string | null,
 *   linkedinOpened: boolean,
 * } | null>
 * ```
 *
 * @author    TopSail AI
 * @version   1.0.0
 * @since     2025-05-19
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* global Quill */

import { TemplateUpdateFlow } from './templateUpdateFlow.js';
import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';
import { htmlToPlainText } from '../utils/htmlToPlainText.js';
import { savePersonalizedMediaFile } from '../utils/personalizedMediaUtils.js';

const HOST_ID = 'linkedinMessageModal';
const LINKEDIN_VIDEO_FILE_NAME = 'topsail-linkedin-video';
let linkedinVideoFileHandle = null;

/**
 * Attempt to copy text to the system clipboard, falling back to a hidden
 * `<textarea>` when the modern API is unavailable.
 *
 * @param {string} value - Message body to copy.
 * @returns {Promise<{ success: boolean, method: string | null }>} Result flag
 *          plus the strategy that succeeded (`"clipboard"` or `"execCommand"`).
 */
async function copyToClipboard(value = '') {
  if (!value) {
    return { success: false, method: 'empty' };
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return { success: true, method: 'clipboard' };
    }
  } catch (err) {
    console.warn('[LinkedInMessageModal] clipboard.writeText failed', err);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return { success: ok, method: ok ? 'execCommand' : 'execCommand_failed' };
  } catch (err) {
    console.warn('[LinkedInMessageModal] execCommand copy failed', err);
    return { success: false, method: 'fallback_failed' };
  }
}

/**
 * Escape HTML entities so template names/contact names render safely in the DOM.
 *
 * @param {string} value - Raw string to escape.
 * @returns {string} HTML-safe string.
 */
function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export async function openLinkedinMessageModal(options = {}) {
  const {
    templateId = '',
    templateIds = [],
    templateData = {},
    mergeVars = {},
    contactName = '',
    contactId = null,
    taskId = null,
    linkedinUrl = '',
    channelLabel = 'LinkedIn Message',
    helperMessage = '',
    placeholder = 'Use this space to craft your LinkedIn message.',
    primaryButtonLabel = 'Copy Message & Open LinkedIn',
    enableTooltips = typeof document !== 'undefined' &&
      document.body?.classList.contains('focusmode') &&
      document.body?.getAttribute('data-focus-tooltips') !== 'false',
  } = options;

  const di = (await import('../bootstrap/app.js')).default;
  const modalService = di.modalService || di.modal;
  const errorUI = di.errorUI;

  const uniqueTemplates = Array.isArray(templateIds)
    ? Array.from(new Set(templateIds.filter(Boolean)))
    : [];
  if (templateId && !uniqueTemplates.includes(templateId)) {
    uniqueTemplates.unshift(templateId);
  }
  let currentTemplateId = templateId || uniqueTemplates[0] || '';
  let messageDraft = '';
  let viewDraft = '';
  let viewHtml = '';
  let rawTemplateHtml = '';
  let templateEditMode = false;
  let updateEnabled = false;
  const copyState = { copied: false, copiedAt: null, method: null };
  let personalizedMediaResult = null;
  let videoThumbnailUrl = '';
  let videoThumbnailBlob = null;
  let linkedInWindow = null;
  let modalClosed = false;

  const templateUpdateFlow = new TemplateUpdateFlow({
    di,
    modalService,
    templateId,
    templateIds: uniqueTemplates,
    templateData,
    mergeVars,
    hostModalId: HOST_ID,
    confirmModalId: 'emailTemplateUpdateConfirmModal',
  });

  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.className = 'modal-overlay hidden';
    document.body.appendChild(host);
    modalService.register(HOST_ID, host);
  }

  const escapedName = contactName ? escapeHtml(contactName) : '';
  const headerSuffix = escapedName ? ` for ${escapedName}` : '';
  const escapedChannelLabel = escapeHtml(channelLabel || 'LinkedIn Message');
  const canUpdateTemplate = uniqueTemplates.length > 0;

  const templateSelectMarkup =
    uniqueTemplates.length > 1
      ? `<div class="flex items-center gap-2 mb-4">
           <label class="text-sm font-medium shrink-0" for="li-message-template">Template</label>
           <select id="li-message-template" class="input flex-1 h-8 px-2 text-sm">
             ${uniqueTemplates
               .map(
                 (id) =>
                   `<option value="${escapeHtml(id)}" ${
                     id === currentTemplateId ? 'selected' : ''
                   }>${escapeHtml(id)}</option>`
               )
               .join('')}
           </select>
         </div>`
      : '';

  const helperText =
    helperMessage ||
    (uniqueTemplates.length
      ? 'Select a template to preview the message. Edit before copying if needed.'
      : 'No template is associated with this step. Draft a message below.');

  host.innerHTML = `
    <div class="modal-container modal-teal linkedin-message-modal">
      <div class="focus-modal__header">
        <div class="focus-modal__title-group">
          <h3>${escapedChannelLabel}${headerSuffix}</h3>
        </div>
        ${
          canUpdateTemplate
            ? `<div class="focus-modal__header-actions">
                 <button type="button" id="li-edit-template" class="btn-pill btn--grey btn--info-sm" data-focus-tooltip="Edit the selected LinkedIn message template.">
                   Edit Template
                 </button>
                 <button type="button" id="li-update-template" class="btn-pill btn--grey btn--info-sm hidden" data-focus-tooltip="Save changes to the selected LinkedIn message template.">
                   Update Template
                 </button>
               </div>`
            : ''
        }
      </div>
      <p
        id="li-template-mode"
        class="focus-modal__mode-banner text-xs text-gray-500 mb-3 hidden"
      >
        Sending is disabled while you edit the template.
      </p>
      <p
        id="li-template-disabled"
        class="focus-modal__mode-banner text-xs text-gray-500 mb-3 hidden"
      >
        Template update unavailable. Check tenant access or template permissions.
      </p>
      ${templateSelectMarkup}
      <p class="text-xs text-gray-500 mb-3" data-empty-message>${escapeHtml(
        helperText
      )}</p>
      <label for="li-message-editor" class="block text-sm font-medium mb-1">Message</label>
      <div id="li-message-editor" class="quill-editor bg-white hidden"></div>
      <textarea id="li-message-text"
                class="input w-full h-48"
                placeholder="${escapeHtml(placeholder)}"></textarea>
      <p class="text-xs text-gray-500 mt-2">
        Topsail will copy this message before opening LinkedIn.
      </p>
      <section class="linkedin-message-video" aria-label="Personalized video">
        <div class="linkedin-message-video__copy" data-video-empty>
          <strong>Personalized video</strong>
          <span data-video-status>Record a quick MP4 for this message.</span>
        </div>
        <div class="linkedin-message-video__ready hidden" data-video-ready>
          <img data-video-thumbnail alt="Personalized video thumbnail" />
          <div class="linkedin-message-video__file">
            <strong data-video-file-name></strong>
            <span data-video-details></span>
            <span data-video-backup></span>
          </div>
        </div>
        <div class="linkedin-message-video__actions">
          <button type="button" class="btn-pill btn--grey btn--info-sm hidden" data-action="save-video-again" data-focus-tooltip="Save this video over the existing local LinkedIn video file.">
            Save Again
          </button>
          <button type="button" class="btn-pill btn--grey btn--info-sm" data-action="record-video" data-focus-tooltip="Record a personalized video and prepare its local LinkedIn attachment.">
            Record Video
          </button>
        </div>
      </section>
      <p class="linkedin-message-video__guide hidden" data-video-guide>
        In LinkedIn, choose <strong>Message</strong> → <strong>Attach</strong>, then select <strong>topsail-linkedin-video.mp4</strong> from Downloads.
      </p>
      <p class="text-xs text-gray-500 mt-2" data-copy-status style="min-height:1.25rem;"></p>
      <footer class="focus-modal__footer mt-4">
        <div class="focus-modal__footer-actions" data-actions="2">
          <button type="button" class="btn-pill btn--grey btn--info-sm btn--secondary-action" data-action="cancel" data-focus-tooltip="Close without saving this LinkedIn message.">Cancel</button>
          <button type="button" class="btn-pill btn--orange btn--info-sm btn--primary-action" data-action="next" data-focus-tooltip="Copy this message and open the contact's LinkedIn profile.">${escapeHtml(
            primaryButtonLabel
          )}</button>
        </div>
      </footer>
    </div>
  `;

  applyFocusModeTooltips(host, { enableTooltips });

  const textarea = host.querySelector('#li-message-text');
  const editor = host.querySelector('#li-message-editor');
  const templateSelect = host.querySelector('#li-message-template');
  const recordVideoBtn = host.querySelector('[data-action="record-video"]');
  const saveVideoAgainBtn = host.querySelector(
    '[data-action="save-video-again"]'
  );
  const videoEmptyEl = host.querySelector('[data-video-empty]');
  const videoReadyEl = host.querySelector('[data-video-ready]');
  const videoThumbnailEl = host.querySelector('[data-video-thumbnail]');
  const videoFileNameEl = host.querySelector('[data-video-file-name]');
  const videoDetailsEl = host.querySelector('[data-video-details]');
  const videoBackupEl = host.querySelector('[data-video-backup]');
  const videoGuideEl = host.querySelector('[data-video-guide]');
  const videoStatusEl = host.querySelector('[data-video-status]');
  const nextBtn = host.querySelector('[data-action="next"]');
  const cancelBtn = host.querySelector('[data-action="cancel"]');
  const editBtn = host.querySelector('#li-edit-template');
  const updateBtn = host.querySelector('#li-update-template');
  const templateModeBanner = host.querySelector('#li-template-mode');
  const templateDisabledBanner = host.querySelector('#li-template-disabled');
  const statusEl = host.querySelector('[data-copy-status]');
  const helperEl = host.querySelector('[data-empty-message]');
  let quill = null;
  let suppressQuillSync = false;

  if (editor) {
    if (!window.Quill) {
      await import(
        'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.min.js'
      );
      if (!document.getElementById('quill-css')) {
        const link = document.createElement('link');
        link.id = 'quill-css';
        link.rel = 'stylesheet';
        link.href =
          'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.snow.css';
        document.head.appendChild(link);
      }
    }

    const toolbarOptions = [
      [{ size: [] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ];

    quill = new Quill(editor, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions,
      },
    });
    quill.root.setAttribute('aria-label', 'LinkedIn template editor');
    quill.on('text-change', () => {
      if (suppressQuillSync) {
        return;
      }
      if (templateEditMode) {
        rawTemplateHtml = quill.root.innerHTML;
        messageDraft = rawTemplateHtml;
        if (textarea) {
          textarea.value = rawTemplateHtml;
        }
        return;
      }
      viewHtml = quill.root.innerHTML;
      viewDraft = htmlToPlainText(viewHtml);
      messageDraft = viewDraft;
      if (textarea) {
        textarea.value = viewDraft;
      }
    });
  }

  /**
   * Update Quill HTML while preventing sync handlers from running.
   *
   * @param {string} html - HTML content to render inside Quill.
   * @returns {void}
   */
  const setQuillHtml = (html) => {
    if (!quill) return;
    suppressQuillSync = true;
    quill.root.innerHTML = html || '';
    suppressQuillSync = false;
  };

  if (editor) {
    editor.classList.remove('hidden');
  }
  if (textarea) {
    textarea.classList.add('hidden');
  }

  /**
   * Render the selected template using TemplateService and populate the textarea.
   *
   * @param {string} id - Template identifier chosen by the seller.
   * @returns {Promise<void>} Resolves once the textarea contains rendered copy.
   */
  const loadTemplate = async (id) => {
    currentTemplateId = id || '';

    if (!currentTemplateId) {
      messageDraft = '';
      viewDraft = '';
      viewHtml = messageDraft;
      rawTemplateHtml = '';
      updateEnabled = false;
      if (textarea) textarea.value = '';
      if (quill) {
        setQuillHtml(viewHtml);
        quill.enable(true);
      }
      if (helperEl)
        helperEl.textContent =
          'No template selected. Draft your LinkedIn message below.';
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', true);
      }
      if (editBtn) {
        editBtn.disabled = true;
      }
      if (updateBtn) {
        updateBtn.disabled = true;
      }
      return;
    }

    try {
      const updateState =
        (await templateUpdateFlow.loadRawTemplate(currentTemplateId)) || {};
      updateEnabled = !!updateState.updateEnabled;
      rawTemplateHtml =
        templateUpdateFlow.getRawTemplateHtml() || rawTemplateHtml;
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', updateEnabled);
      }
      if (editBtn) {
        editBtn.disabled = !updateEnabled;
      }
      if (updateBtn) {
        updateBtn.disabled = !updateEnabled;
      }
      const ctx = { ...(mergeVars || {}), ...(templateData || {}) };
      const rendered = await di.templateService.render(currentTemplateId, ctx);
      viewHtml = rendered.html || '';
      viewDraft = htmlToPlainText(rendered.html || '');
      if (!templateEditMode) {
        messageDraft = viewDraft;
        if (textarea) {
          textarea.value = messageDraft;
        }
        if (quill) {
          setQuillHtml(viewHtml);
          quill.enable(true);
        }
      }
      if (helperEl)
        helperEl.textContent =
          'Review the message below, tweak if needed, then copy it.';
    } catch (err) {
      console.error('[LinkedInMessageModal] render failed', err);
      messageDraft = '';
      viewDraft = '';
      viewHtml = '';
      updateEnabled = false;
      if (textarea) textarea.value = '';
      if (quill) {
        setQuillHtml('');
        quill.enable(true);
      }
      if (helperEl)
        helperEl.textContent =
          'Unable to load the selected template. Draft your own message below.';
      if (templateDisabledBanner) {
        templateDisabledBanner.classList.toggle('hidden', true);
      }
      if (editBtn) {
        editBtn.disabled = true;
      }
      if (updateBtn) {
        updateBtn.disabled = true;
      }
      errorUI?.global?.({
        text: 'Unable to render LinkedIn message template.',
        type: 'error',
        duration: 4000,
      });
    }
  };

  const detach = [];

  if (textarea) {
    /**
     * Keep the in-memory draft synchronised with user edits.
     *
     * @param {Event} ev - Input event dispatched by the textarea.
     * @returns {void}
     */
    function onInput(ev) {
      messageDraft = ev.target.value;
      if (templateEditMode) {
        rawTemplateHtml = messageDraft;
      } else {
        viewDraft = messageDraft;
      }
    }
    textarea.addEventListener('input', onInput);
    detach.push(() => textarea.removeEventListener('input', onInput));
  }

  if (templateSelect) {
    /**
     * Handle template switching by re-rendering the selected option.
     *
     * @param {Event} ev - Change event fired by the `<select>` element.
     * @returns {void}
     */
    const onChange = async (ev) => {
      const nextTemplateId = ev.target.value;
      await loadTemplate(nextTemplateId);
      await setTemplateEditMode(false);
    };
    templateSelect.addEventListener('change', onChange);
    detach.push(() => templateSelect.removeEventListener('change', onChange));
  }

  /**
   * Release the thumbnail URL created for the currently selected local video.
   *
   * @returns {void}
   */
  const releaseVideoThumbnail = () => {
    if (videoThumbnailUrl) {
      globalThis.URL?.revokeObjectURL?.(videoThumbnailUrl);
    }
    videoThumbnailUrl = '';
    videoThumbnailBlob = null;
  };

  /**
   * Render the locally ready attachment and its independent secure-backup state.
   *
   * @param {object|null} mediaResult Recorder result to display.
   * @returns {void}
   */
  const renderPersonalizedMediaResult = (mediaResult) => {
    const ready = Boolean(mediaResult?.localExported && mediaResult?.fileName);
    videoEmptyEl?.classList.toggle('hidden', ready);
    videoReadyEl?.classList.toggle('hidden', !ready);
    saveVideoAgainBtn?.classList.toggle('hidden', !ready);
    videoGuideEl?.classList.toggle('hidden', !ready);
    if (recordVideoBtn) {
      recordVideoBtn.textContent = ready ? 'Replace' : 'Record Video';
    }
    if (!ready) return;

    if (
      mediaResult.thumbnail instanceof Blob &&
      mediaResult.thumbnail !== videoThumbnailBlob
    ) {
      releaseVideoThumbnail();
      videoThumbnailBlob = mediaResult.thumbnail;
      videoThumbnailUrl =
        globalThis.URL?.createObjectURL?.(mediaResult.thumbnail) || '';
      if (videoThumbnailEl) videoThumbnailEl.src = videoThumbnailUrl;
    }
    videoThumbnailEl?.classList.toggle('hidden', !videoThumbnailUrl);
    if (videoFileNameEl) videoFileNameEl.textContent = mediaResult.fileName;
    if (videoDetailsEl) {
      videoDetailsEl.textContent = `${
        mediaResult.durationLabel || 'Video ready'
      } · Ready in Downloads`;
    }
    if (videoBackupEl) {
      videoBackupEl.textContent =
        mediaResult.uploadStatus === 'ready'
          ? 'Secure backup complete.'
          : mediaResult.uploadStatus === 'failed'
            ? 'Secure backup failed; the local attachment is still ready.'
            : 'Backing up securely in the background…';
    }
  };

  if (recordVideoBtn) {
    /**
     * Open the shared recorder and retain its ready asset for this LinkedIn step.
     *
     * @returns {Promise<void>}
     */
    const onRecordVideo = async () => {
      recordVideoBtn.disabled = true;
      if (videoStatusEl) videoStatusEl.textContent = 'Opening camera…';
      try {
        const {
          formatPersonalizedMediaDuration,
          openPersonalizedVideoRecorderModal,
        } = await import('./personalizedVideoRecorderModal.js');
        const nextMediaResult = await openPersonalizedVideoRecorderModal({
          contactId,
          taskId,
          channel: 'linkedin',
          requireMp4: true,
          fileName: LINKEDIN_VIDEO_FILE_NAME,
          overwriteLocalFile: true,
          localFileHandle: linkedinVideoFileHandle,
        });
        if (nextMediaResult?.localExported) {
          personalizedMediaResult = nextMediaResult;
          linkedinVideoFileHandle =
            nextMediaResult.localFileHandle || linkedinVideoFileHandle;
          personalizedMediaResult.durationLabel =
            formatPersonalizedMediaDuration(
              personalizedMediaResult.durationMs || 0
            );
          renderPersonalizedMediaResult(personalizedMediaResult);
          personalizedMediaResult.uploadPromise?.then((asset) => {
            if (modalClosed || personalizedMediaResult !== nextMediaResult) {
              return;
            }
            personalizedMediaResult.asset =
              asset || personalizedMediaResult.asset || null;
            personalizedMediaResult.uploadStatus = asset ? 'ready' : 'failed';
            renderPersonalizedMediaResult(personalizedMediaResult);
          });
        } else if (personalizedMediaResult?.localExported) {
          renderPersonalizedMediaResult(personalizedMediaResult);
        } else if (videoStatusEl) {
          videoStatusEl.textContent =
            'No video selected. Record one whenever you are ready.';
        }
      } catch (err) {
        console.error('[LinkedInMessageModal] video recorder failed', err);
        if (videoStatusEl) {
          videoStatusEl.textContent =
            'Unable to prepare the video. Check camera permissions and try again.';
        }
        errorUI?.global?.({
          text: 'Unable to prepare personalized video.',
          type: 'error',
          duration: 4000,
        });
      } finally {
        recordVideoBtn.disabled = templateEditMode;
        if (host.classList.contains('hidden')) modalService.open(HOST_ID);
      }
    };
    recordVideoBtn.addEventListener('click', onRecordVideo);
    detach.push(() =>
      recordVideoBtn.removeEventListener('click', onRecordVideo)
    );
  }

  if (saveVideoAgainBtn) {
    /**
     * Save the current bytes over the reusable local LinkedIn video file.
     *
     * @returns {Promise<void>}
     */
    const onSaveVideoAgain = async () => {
      if (!personalizedMediaResult?.file) return;
      try {
        const localSaveResult = await savePersonalizedMediaFile(
          personalizedMediaResult.file,
          {
            fileName: personalizedMediaResult.fileName,
            overwriteExisting: true,
            fileHandle: linkedinVideoFileHandle,
          }
        );
        personalizedMediaResult.fileName = localSaveResult.fileName;
        linkedinVideoFileHandle =
          localSaveResult.fileHandle || linkedinVideoFileHandle;
        if (statusEl) {
          statusEl.textContent = `Updated ${personalizedMediaResult.fileName}.`;
        }
      } catch (error) {
        console.error(
          '[LinkedInMessageModal] local video export failed',
          error
        );
        errorUI?.global?.({
          text: 'Unable to update the local video file.',
          type: 'error',
          duration: 4000,
        });
      }
    };
    saveVideoAgainBtn.addEventListener('click', onSaveVideoAgain);
    detach.push(() =>
      saveVideoAgainBtn.removeEventListener('click', onSaveVideoAgain)
    );
  }

  /**
   * Toggle template edit mode for updating the stored template source.
   *
   * @param {boolean} nextMode - When true, switch into template edit mode.
   * @returns {Promise<void>} Resolves once the UI reflects the requested mode.
   */
  const setTemplateEditMode = async (nextMode) => {
    templateEditMode = nextMode;
    if (editBtn) {
      editBtn.textContent = templateEditMode
        ? 'Cancel Update'
        : 'Edit Template';
    }
    if (updateBtn) {
      updateBtn.classList.toggle('hidden', !templateEditMode);
    }
    if (templateModeBanner) {
      templateModeBanner.classList.toggle('hidden', !templateEditMode);
    }
    if (nextBtn) {
      nextBtn.disabled = templateEditMode;
    }
    if (recordVideoBtn) {
      recordVideoBtn.disabled = templateEditMode;
    }
    if (saveVideoAgainBtn) {
      saveVideoAgainBtn.disabled = templateEditMode;
    }
    if (templateEditMode) {
      rawTemplateHtml =
        templateUpdateFlow.getRawTemplateHtml() || rawTemplateHtml;
      if (textarea) {
        textarea.value = rawTemplateHtml;
        textarea.classList.add('hidden');
      }
      if (quill) {
        setQuillHtml(rawTemplateHtml);
        quill.enable(true);
      }
      messageDraft = rawTemplateHtml;
      if (helperEl) {
        helperEl.textContent =
          'Editing template source. Update to save changes or cancel to discard.';
      }
      return;
    }

    rawTemplateHtml =
      quill?.root?.innerHTML ?? textarea?.value ?? rawTemplateHtml;
    messageDraft = viewDraft;
    if (textarea) {
      textarea.value = viewDraft;
      textarea.classList.add('hidden');
    }
    if (quill) {
      setQuillHtml(viewHtml);
      quill.enable(true);
    }
    if (helperEl) {
      helperEl.textContent =
        'Review the message below, tweak if needed, then copy it.';
    }
  };

  /**
   * Handle toggling the template edit mode.
   * @returns {Promise<void>}
   */
  const onEditTemplate = async () => {
    if (!updateEnabled) {
      errorUI?.global?.({
        text: 'Template updates are unavailable for this step.',
        type: 'warning',
        duration: 3000,
      });
      return;
    }
    await setTemplateEditMode(!templateEditMode);
  };

  /**
   * Persist the edited template source and refresh the rendered view.
   * @returns {Promise<void>}
   */
  const onUpdateTemplate = async () => {
    if (!currentTemplateId) return;
    if (!updateEnabled) {
      errorUI?.global?.({
        text: 'Template updates are unavailable for this step.',
        type: 'warning',
        duration: 3000,
      });
      return;
    }
    if (!templateEditMode) {
      errorUI?.global?.({
        text: 'Switch to Edit Template to update the saved template.',
        type: 'info',
        duration: 3000,
      });
      return;
    }
    if (updateBtn) {
      updateBtn.disabled = true;
      updateBtn.textContent = 'Updating…';
    }
    const rawHtml =
      quill?.root?.innerHTML ?? textarea?.value ?? rawTemplateHtml;
    try {
      const result = await templateUpdateFlow.confirmAndUpdate({
        html: rawHtml,
        templateId: currentTemplateId,
      });
      if (host.classList.contains('hidden')) {
        modalService.open(HOST_ID);
      }
      if (!result || result.updated !== true) {
        if (result && result.reason === 'missing-tenant-id') {
          errorUI?.global?.({
            text: 'Cannot update template because tenant information is missing.',
            type: 'error',
            duration: 4000,
          });
        }
        return;
      }
      const ctx = { ...(mergeVars || {}), ...(templateData || {}) };
      if (di?.templateService?.renderFromSource) {
        const rendered = await di.templateService.renderFromSource(
          { html: rawHtml },
          ctx
        );
        viewHtml = rendered.html || '';
        viewDraft = htmlToPlainText(rendered.html || '');
        messageDraft = viewDraft;
        if (textarea) {
          textarea.value = viewDraft;
        }
        if (quill) {
          setQuillHtml(viewHtml);
        }
      } else {
        await loadTemplate(currentTemplateId);
      }
      await setTemplateEditMode(false);
      quill?.root?.focus?.();
      errorUI?.global?.({
        text: 'Template updated.',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('[LinkedInMessageModal] template update failed:', err);
      errorUI?.global?.({
        text: 'Failed to update template. Check permissions and try again.',
        type: 'error',
        duration: 4000,
      });
    } finally {
      if (updateBtn) {
        updateBtn.disabled = false;
        updateBtn.textContent = 'Update Template';
      }
    }
  };

  /**
   * Close the modal and run all registered cleanup callbacks.
   * @returns {void}
   */
  const closeModal = () => {
    modalClosed = true;
    try {
      modalService.close(HOST_ID);
    } catch (err) {
      console.warn('[LinkedInMessageModal] close failed', err);
    }
    detach.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        console.warn('[LinkedInMessageModal] listener cleanup failed', e);
      }
    });
    releaseVideoThumbnail();
  };

  return new Promise((resolve) => {
    /**
     * Resolve the pending promise while ensuring the modal is closed.
     * @param {object|null} value - Data returned to the caller.
     * @returns {void}
     */
    const settle = (value) => {
      closeModal();
      resolve(value);
    };

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => settle(null), { once: true });
    }

    if (nextBtn) {
      /**
       * Copy the current draft and open LinkedIn within the same user gesture.
       *
       * @returns {Promise<void>}
       */
      const onPrepareLinkedIn = async () => {
        if (templateEditMode) return;
        const originalLabel = primaryButtonLabel;
        nextBtn.disabled = true;
        nextBtn.textContent = 'Preparing…';
        messageDraft = htmlToPlainText(
          quill?.root?.innerHTML ?? textarea?.value ?? messageDraft ?? ''
        );

        const copyPromise = copyToClipboard(messageDraft);
        let linkedinOpened = false;
        if (linkedinUrl) {
          try {
            if (linkedInWindow && linkedInWindow.closed === false) {
              linkedInWindow.focus?.();
            } else {
              linkedInWindow = globalThis.open?.(linkedinUrl, '_blank') || null;
              if (linkedInWindow) linkedInWindow.opener = null;
            }
            linkedinOpened = Boolean(linkedInWindow);
          } catch (error) {
            console.warn('[LinkedInMessageModal] LinkedIn open failed', error);
            linkedInWindow = null;
          }
        }

        const { success, method } = await copyPromise;
        copyState.copied = success;
        copyState.copiedAt = success ? Date.now() : null;
        copyState.method = method;

        if (linkedinUrl && !linkedinOpened) {
          if (statusEl) {
            statusEl.textContent = success
              ? 'Message copied, but your browser blocked LinkedIn. Allow the popup and click again.'
              : 'Your browser blocked LinkedIn. Allow the popup and click again.';
          }
          nextBtn.disabled = false;
          nextBtn.textContent = originalLabel;
          errorUI?.global?.({
            text: 'Allow the LinkedIn popup, then try again.',
            type: 'warning',
            duration: 5000,
          });
          return;
        }

        if (messageDraft && !success) {
          if (statusEl) {
            statusEl.textContent =
              'LinkedIn opened, but the message could not be copied. Return here and try again.';
          }
          nextBtn.disabled = false;
          nextBtn.textContent = 'Try Copy Again';
          errorUI?.global?.({
            text: 'Unable to copy the LinkedIn message.',
            type: 'warning',
            duration: 5000,
          });
          return;
        }

        settle({
          templateId: currentTemplateId || null,
          message: messageDraft,
          copied: copyState.copied,
          copiedAt: copyState.copiedAt,
          copyMethod: copyState.method,
          linkedinOpened,
          personalizedMediaAsset: personalizedMediaResult?.asset || null,
          personalizedMediaLocalExported: Boolean(
            personalizedMediaResult?.localExported
          ),
          personalizedMediaUploadPromise:
            personalizedMediaResult?.uploadPromise || null,
        });
      };
      nextBtn.addEventListener('click', onPrepareLinkedIn);
      detach.push(() =>
        nextBtn.removeEventListener('click', onPrepareLinkedIn)
      );
    }

    if (editBtn) {
      editBtn.addEventListener('click', onEditTemplate);
      detach.push(() => editBtn.removeEventListener('click', onEditTemplate));
    }

    if (updateBtn) {
      updateBtn.addEventListener('click', onUpdateTemplate);
      detach.push(() =>
        updateBtn.removeEventListener('click', onUpdateTemplate)
      );
    }

    try {
      modalService.open(HOST_ID);
    } catch (err) {
      console.warn('[LinkedInMessageModal] open failed', err);
    }

    loadTemplate(currentTemplateId);
    quill?.root?.focus?.();
  });
}
